import { redirect } from 'react-router';

import { type UserPermission } from '@workinance/shared/data/permissions';
import { UserStatusEnum } from '@workinance/shared/interfaces/user.interface';

import { canNavigate, isOwner, useAuthStore } from '@/store/auth.store';

// Route guards, run as loaders: the router checks them before a page renders (or its code downloads) and
// redirects instead. The router runs the loaders of nested routes at the same time, not parent first, so
// every guard waits for the saved session itself rather than relying on its parent to have done so.

let sessionRestoration: Promise<void> | null = null;

/** Reads the saved session from the device once per page load; every guard waits on the same read. */
function ensureSessionRestored() {
	sessionRestoration ??= useAuthStore.getState().restoreAuthentication();
	return sessionRestoration;
}

/** The root route: nothing renders (the loading screen shows) until the saved session is read. */
export async function restoreSessionLoader() {
	await ensureSessionRestored();
	return null;
}

/** Keeps signed-in users off the guest-only login page. Register and forgot-password live in the admin app only. */
export async function publicOnlyLoader() {
	await ensureSessionRestored();
	if (useAuthStore.getState().isAuthenticated) return redirect('/');
	return null;
}

/** Guards every signed-in-only route. */
export async function requireAuthLoader() {
	await ensureSessionRestored();
	if (!useAuthStore.getState().isAuthenticated) return redirect('/login');
	return null;
}

/** Only users who still have to finish setting up their account belong on the activation page. */
export async function activateLoader() {
	await ensureSessionRestored();
	if (useAuthStore.getState().user?.status !== UserStatusEnum.INVITED) {
		return redirect('/');
	}
	return null;
}

/** An invited user can only reach the activation page until they have set their own password. */
export async function requireActivatedLoader() {
	await ensureSessionRestored();
	if (useAuthStore.getState().user?.status === UserStatusEnum.INVITED) {
		return redirect('/activate');
	}
	return null;
}

/** Every dashboard request is scoped to a business: team members sign in with one and owners pick one. A session without one is cleared. */
export async function requireBusinessLoader() {
	await ensureSessionRestored();
	const { isAuthenticated, business, logout } = useAuthStore.getState();
	if (isAuthenticated && !business) {
		logout();
		return redirect('/login');
	}
	return null;
}

/** The POS screen is only reachable once a business has turned the POS app on for itself. */
export async function requirePosEnabledLoader() {
	await ensureSessionRestored();
	if (!useAuthStore.getState().business?.settings.pos.isEnabled) {
		return redirect('/dashboard');
	}
	return null;
}

/**
 * A route gated by permissions: owners and whoever holds at least one of `requiredPermissions` may open
 * it; anyone else goes back to the dashboard. Pass the same list the page's nav item declares
 * (`requiredPermissions` in `navigation.ts`), so there is one place that says what a route needs.
 */
export function requireNavigationPermissionLoader(
	requiredPermissions?: UserPermission[],
) {
	return async () => {
		await ensureSessionRestored();
		const { user, hasLoadedPermissions, loadPermissions } =
			useAuthStore.getState();
		if (!user) return null;
		// A team member's permissions are fetched after the session is restored; wait for them rather than turn them away.
		if (!isOwner(user) && !hasLoadedPermissions) await loadPermissions();
		if (!canNavigate(requiredPermissions)) return redirect('/dashboard');
		return null;
	};
}

/**
 * For a parent route (such as `/crm`) that only redirects to one of its children: redirects to the
 * first `to` in `routes` the signed-in user actually holds `requiredPermissions` for, instead of a
 * single hardcoded default that can land someone on a page they cannot use even though a sibling
 * page would have worked. `/crm`'s own loader already requires at least one of these permissions,
 * so falling through every option here should not happen in practice; `/dashboard` is the fallback
 * if it ever does.
 */
export function requireFirstAccessibleRouteLoader(
	routes: { to: string; requiredPermissions?: UserPermission[] }[],
) {
	return async () => {
		await ensureSessionRestored();
		const { user, hasLoadedPermissions, loadPermissions } =
			useAuthStore.getState();
		if (user && !isOwner(user) && !hasLoadedPermissions) await loadPermissions();

		const accessibleRoute = routes.find((route) =>
			canNavigate(route.requiredPermissions),
		);
		return redirect(accessibleRoute?.to ?? '/dashboard');
	};
}
